// src/pages/Expedientes/ExpedientesHome.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    Paper, Box, TextField, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, TablePagination, TableSortLabel,
    Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
    Checkbox, Chip, Typography, Tabs, Tab,
    FormControl, InputLabel, Select, MenuItem, OutlinedInput,
    Collapse, Radio, RadioGroup, FormControlLabel, InputAdornment,
} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Undo, GetApp } from "@mui/icons-material";
import MuiAlert from '@mui/material/Alert';
import ExpedientesHomeBreadcrumb from "../../components/ExpedientesHomeBreadcrumb";
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import SelectorListas from '../../components/ListasTrabajo/Compartidos/SelectorListas';
import BuscadorPersonas from '../../components/ListasTrabajo/BusquedaAvanzada/BuscadorPersonas';
import { nodeApiPath } from "../../utils/paths";
import { expedientesBusquedaUnificadaJWT, expedientesIdsJWT, busquedaAvanzadaJWT, getCombosJWT, permisosUsuarioJWT, expedienteEliminarJWT } from "../../api/endpoints";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpedienteForm from "./ExpedienteForm";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionIcon from "@mui/icons-material/Description";
import * as XLSX from 'xlsx';
import { Skeleton } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getBase64FromImage } from './HistorialCambios';
import logoSIGIF from '../../assets/sigifblue.png';

const filtrosVisiblesJudicial = [
    { name: "autos", label: "Autos" },
    { name: "caratula", label: "Carátula" },
    { name: "certificado", label: "Nº Certificado" },
    { name: "nro_carta_poder", label: "Nº Carta Poder" }
];

// Columnas primarias (siempre visibles) — sin Estado/Ver/Eliminar que se agregan aparte
const primaryColsJudicial = [
    { title: "Autos", field: "autos" },
    { title: "Carátula", field: "caratula" },
];
const primaryColsAdmin = [
    { title: "Expediente", field: "nro_expediente" },
    { title: "Extracto", field: "extracto" },
    { title: "Iniciador", field: "iniciador" },
];

// Columnas default (se muestran si no hay filtros que las reemplacen)
const defaultOptionalJudicial = [
    { title: "Tipo Proceso", field: "tipo_proceso" },
    // Certif se agrega dinámicamente solo si hay datos reales
];
const defaultOptionalAdmin = [];

// Mapa: filtro activo → columna dinámica que se agrega
const filterColumnMap = {
    juzgado:              { title: "Juzgado",        field: "juzgado_nombre" },
    fechaAsignacionDesde: { title: "F. Asignación",  field: "fecha_asignacion_fmt" },
    fechaAsignacionHasta: { title: "F. Asignación",  field: "fecha_asignacion_fmt" },
    fechaInicioDesde:     { title: "F. Inicio",      field: "fecha_inicio_fmt" },
    fechaInicioHasta:     { title: "F. Inicio",      field: "fecha_inicio_fmt" },
    montoDesde:           { title: "Monto",          field: "monto_total" },
    montoHasta:           { title: "Monto",          field: "monto_total" },
    etapasProcesales:     { title: "Etapa Procesal", field: "etapa_procesal" },
    objetoReclamo:        { title: "Obj. Reclamo",   field: "objeto_reclamo" },
    reparticionOrigen:    { title: "Repartición",    field: "reparticion_origen" },
    persona:              { title: "Demandado",      field: "demandado" },
    dni:                  { title: "Demandado",      field: "demandado" },
    idPersonal:           { title: "Demandado",      field: "demandado" },
    resolucionNumero:        { title: "Res. Nº",         field: "res_numero" },
    resolucionDescripcion:   { title: "Res. Desc.",      field: "res_descripcion" },
    resolucionNroExpediente: { title: "Res. Nº Exp.",    field: "res_nro_expediente" },
    resolucionOrigen:        { title: "Res. Origen",     field: "res_origen" },
    // Filtros de movimientos → muestran Últ. mov. (fecha + tipo).
    // El backend agrega las columnas Ultimo_Mov_* cuando hay alguno de estos filtros.
    conMovimientos:       { title: "Últ. mov.",     field: "ultimo_mov_fecha" },
    tipoMovimiento:       { title: "Últ. mov.",     field: "ultimo_mov_fecha" },
    fechaMovDesde:        { title: "Últ. mov.",     field: "ultimo_mov_fecha" },
    fechaMovHasta:        { title: "Últ. mov.",     field: "ultimo_mov_fecha" },
};

const filtrosVisiblesAdmin = [
    { name: "nro_expediente", label: "Nº Expediente" },
    { name: "extracto", label: "Extracto" },
    { name: "iniciador", label: "Iniciador" },
];

const SS_FILTROS_KEY = 'sigif_home_filtros';
const SS_TAB_KEY = 'sigif_home_tab';

function getInitialFiltros(tab) { return {}; }
function formatFecha(val) {
    if (!val) return "-";
    // .NET: /Date(1234567890)/
    const match = /\/Date\((\d+)\)\//.exec(String(val));
    if (match) return new Date(Number(match[1])).toLocaleDateString('es-AR');
    // ISO string o Date
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString('es-AR');
}

const isEmptyFiltros = (obj) =>
    !obj || Object.values(obj).every(v => v === "" || v === null || v === undefined);

// Normalizar MAYÚSCULAS → Título
const toTitleCase = (str) => {
    if (!str) return '';
    // Si ya está en formato mixto (tiene minúsculas), no tocar
    if (str !== str.toUpperCase()) return str;
    return str.toLowerCase().replace(/(?:^|\s|[-/(])\S/g, c => c.toUpperCase());
};

// Resaltar coincidencia de búsqueda
const highlightMatch = (text, query) => {
    if (!query || query.length < 2 || !text) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
        <>{text.slice(0, idx)}<strong style={{ color: '#1565c0' }}>{text.slice(idx, idx + query.length)}</strong>{text.slice(idx + query.length)}</>
    );
};

// Estilo zebra para opciones
const zebraStyle = (index) => ({
    backgroundColor: index % 2 === 1 ? '#f5f7fa' : 'transparent',
});

// case-insensitive: toma el primer campo disponible
const pickAny = (row, keys) => {
    if (!row) return "";
    const lower = Object.fromEntries(Object.keys(row).map(k => [k.toLowerCase(), k]));
    for (const k of keys) {
        const real = lower[k.toLowerCase()];
        if (real !== undefined) {
            const val = row[real];
            if (val !== null && val !== undefined) {
                const s = String(val).trim();
                if (s !== "") return s;
            }
        }
    }
    return "";
};


function TableSkeleton({ rows = 5, columns = 6 }) {
    return (
        <>
            {[...Array(rows)].map((_, index) => (
                <TableRow key={index}>
                    {[...Array(columns)].map((_, colIndex) => (
                        <TableCell key={colIndex}>
                            <Skeleton
                                variant="text"
                                height={20}
                                animation="wave"
                                style={{ borderRadius: 4 }}
                            />
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </>
    );
}


// Campos que SOLO sabe manejar busquedaAvanzada (POST). Si hay alguno cargado, la
// búsqueda va por ese camino en vez del unificado (GET).
const CAMPOS_AVANZADOS = [
    'tiposProceso', 'estadosProceso',
    'fechaAsignacionDesde', 'fechaAsignacionHasta', 'fechaInicioDesde', 'fechaInicioHasta',
    'montoDesde', 'montoHasta', 'etapasProcesales', 'objetoReclamo',
    'sinCertificados', 'certificadoDesde', 'certificadoHasta', 'cuenta', 'reparticion', 'impuesto', 'planPagoEstado',
    'persona', 'dni', 'caracterPersona', 'idPersonal',
    'sinJuzgados', 'juzgado',
    'conMovimientos', 'tipoMovimiento', 'fechaMovDesde', 'fechaMovHasta', 'textoMovimiento',
    'conCartaPoder',
    'interesFiscal',
    // Admin-específicos (reparticionOrigen solo vía busquedaAvanzada)
    'reparticionOrigen',
    // Resoluciones
    'resolucionNumero', 'resolucionDescripcion', 'resolucionNroExpediente', 'resolucionOrigen',
];

function tieneFiltrosAvanzados(f) {
    return CAMPOS_AVANZADOS.some(k => {
        const v = (f || {})[k];
        if (v === undefined || v === null || v === '' || v === false) return false;
        if (Array.isArray(v)) return v.length > 0;
        return true;
    });
}

/**
 * Normaliza las filas que devuelve el backend al formato que espera la tabla.
 * Está afuera del componente porque la usan dos caminos: la búsqueda paginada
 * y el export, que se trae el resultado completo aparte.
 */
function mapearFilas(items) {
    return (items || []).map(exp => {
        const certificadoVal = pickAny(exp, [
            "certificado", "certif", "nro_certificado", "Nro_Certificado", "nroCertificado"
        ]);
        const montoRaw = pickAny(exp, ['monto_total', 'Monto_Total', 'monto']);
        const montoNum = montoRaw ? Number(montoRaw) : null;

        return {
            ...exp,
            id: exp.id || exp.Id,
            autos: pickAny(exp, ['autos', 'Autos', 'numero']) || "-",
            caratula: pickAny(exp, ['caratula', 'Caratula']) || "-",
            tipo_proceso: pickAny(exp, ['tipoProceso', 'tipo_proceso', 'Tipo_Proceso']) || "-",
            certificado: certificadoVal || "-",
            nro_expediente: pickAny(exp, ['nro_expediente', 'expediente_Adm', 'Expediente_Adm', 'numero']) || exp.id || exp.Id,
            estado: pickAny(exp, ['estado', 'Estado', 'estados_Proceso']) || "-",
            extracto: pickAny(exp, ['extracto', 'Extracto']) || "-",
            iniciador: pickAny(exp, ['iniciador', 'Iniciador']) || "-",
            // Campos dinámicos
            juzgado_nombre: pickAny(exp, ['juzgado', 'Juzgado', 'juzgado_nombre']) || "-",
            fecha_asignacion_fmt: formatFecha(pickAny(exp, ['fecha_Asignacion', 'Fecha_Asignacion', 'fechaAsignacion'])),
            fecha_inicio_fmt: formatFecha(pickAny(exp, ['fecha_Inicio', 'Fecha_Inicio', 'fechaInicio'])),
            monto_total: montoNum != null && !isNaN(montoNum) ? `$${montoNum.toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : "-",
            etapa_procesal: pickAny(exp, ['etapa_Procesal', 'Etapa_Procesal', 'etapaProcesal', 'etapa']) || "-",
            demandado: pickAny(exp, ['demandado', 'Demandado']) || "-",
            reparticion_origen: pickAny(exp, ['reparticion_Origen', 'Reparticion_Origen', 'reparticionOrigen']) || "-",
            objeto_reclamo: pickAny(exp, ['Objeto_Reclamo', 'objeto_Reclamo', 'objetoReclamo', 'objeto_reclamo']) || "-",
            res_numero: pickAny(exp, ['Res_Numero']) || "",
            res_descripcion: pickAny(exp, ['Res_Descripcion']) || "",
            res_nro_expediente: pickAny(exp, ['Res_NroExpediente']) || "",
            res_origen: pickAny(exp, ['Res_Origen']) || "",
            // Último movimiento (lo trae el backend si se filtró por movs).
            // Mostramos "fecha — tipo" en una sola celda para no comer ancho.
            ultimo_mov_fecha: (() => {
                const f = pickAny(exp, ['Ultimo_Mov_Fecha', 'ultimo_Mov_Fecha', 'ultimo_mov_fecha']);
                const t = pickAny(exp, ['Ultimo_Mov_Tipo', 'ultimo_Mov_Tipo', 'ultimo_mov_tipo']);
                const fechaFmt = f ? formatFecha(f) : '';
                if (!fechaFmt && !t) return '-';
                return [fechaFmt, t].filter(Boolean).join(' — ');
            })(),
            ultimo_mov_descripcion: pickAny(exp, ['Ultimo_Mov_Descripcion', 'ultimo_Mov_Descripcion', 'ultimo_mov_descripcion']) || "",
        };
    });
}

export default function ExpedientesHome({ usuario, moduloActivo }) {
    const [tab, setTab] = useState(() => {
        try { const ss = sessionStorage.getItem(SS_TAB_KEY); return ss !== null ? parseInt(ss, 10) : 0; } catch { return 0; }
    });
    const [filtros, setFiltros] = useState(() => {
        try { const ss = sessionStorage.getItem(SS_FILTROS_KEY); return ss ? JSON.parse(ss) : {}; } catch { return {}; }
    });
    const [resultados, setResultados] = useState(() => {
        try { const ss = sessionStorage.getItem('sigif_home_resultados'); return ss ? JSON.parse(ss) : []; } catch { return []; }
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    // Total REAL de coincidencias en el servidor, no la cantidad de la página visible.
    const [totalResultados, setTotalResultados] = useState(() => {
        try { const ss = sessionStorage.getItem('sigif_home_total'); return ss ? parseInt(ss, 10) : 0; } catch { return 0; }
    });
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [exportando, setExportando] = useState(false);
    const [orderBy, setOrderBy] = useState("");
    const [order, setOrder] = useState("asc");
    const [lastFiltros, setLastFiltros] = useState(null);
    const [lastResultados, setLastResultados] = useState(null);
    const [lastTotal, setLastTotal] = useState(0);

    const [tiposMovimiento, setTiposMovimiento] = useState([]);
    const [objetosReclamo, setObjetosReclamo] = useState([]);
    const [estadosPlanPago, setEstadosPlanPago] = useState([]);

    const [reparticiones, setReparticiones] = useState([]);
    const [reparticionesTodas, setReparticionesTodas] = useState([]);
    const [impuestos, setImpuestos] = useState([]);

    // Catálogos para resolución de chips
    const [catTiposProceso, setCatTiposProceso] = useState([]);
    const [catEstadosProceso, setCatEstadosProceso] = useState([]);
    const [catEtapasProcesales, setCatEtapasProcesales] = useState([]);
    const [catJuzgados, setCatJuzgados] = useState([]);
    // Módulos globales del sistema (catálogo). Se usan como fallback cuando el
    // usuario no tiene módulos propios cargados, para que el filtro Área aparezca igual.
    const [modulosGlobales, setModulosGlobales] = useState([]);

    const [expedientesSeleccionados, setExpedientesSeleccionados] = useState([]);

    // eslint-disable-next-line no-unused-vars
    const [forbidden, setForbidden] = useState(false);
    const [permisos, setPermisos] = useState({ insert: false, update: false, delete: false });
    const [deleteDlg, setDeleteDlg] = useState({ open: false, id: null, label: '' });

    // NUEVO: para Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, msg: "", undo: false, color: "info" });

    const snackbarTimer = useRef(null);

    // Función para toggle selección
    const handleToggleSeleccion = (id) => {
        setExpedientesSeleccionados(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    const [mostrarSelectorListas, setMostrarSelectorListas] = useState(false);

    // Checkbox de la cabecera: alcanza SOLO a la pagina visible. Con paginado de
    // servidor eso son 25 filas, no las 100-500 que se cargaban antes — por eso existe
    // el boton "seleccionar los N que coinciden" de al lado.
    const handleToggleTodos = () => {
        if (expedientesSeleccionados.length === resultados.length) {
            setExpedientesSeleccionados([]);
        } else {
            setExpedientesSeleccionados(resultados.map(r => r.id));
        }
    };

    // Trae del servidor los ids de TODO lo que coincide con la busqueda actual, no solo
    // los de la pagina. Sin esto, agregar a una lista desde una busqueda de 3.000
    // resultados agregaba 25.
    const [seleccionandoTodos, setSeleccionandoTodos] = useState(false);
    const handleSeleccionarCoincidentes = async () => {
        setSeleccionandoTodos(true);
        try {
            const tipoExp = tab === 0 ? 1 : 2;
            const data = tieneFiltrosAvanzados(filtros)
                ? await busquedaAvanzadaJWT(filtros, tipoExp, 0, 0, true)
                : await expedientesIdsJWT({ tipo: tipoExp, ...filtros });
            if (!data.ok) throw new Error(data.mensaje || 'No se pudieron traer los expedientes');

            const ids = (data.expedientes || []).map(e => e.Id ?? e.id).filter(Boolean);
            setExpedientesSeleccionados(ids);

            setSnackbar({
                open: true,
                color: data.total > ids.length ? 'warning' : 'success',
                msg: data.total > ids.length
                    ? `Se seleccionaron ${ids.length.toLocaleString('es-AR')} de ${data.total.toLocaleString('es-AR')}. Es el máximo por operación; afiná los filtros para abarcar el resto.`
                    : `${ids.length.toLocaleString('es-AR')} expedientes seleccionados`,
            });
        } catch (err) {
            setSnackbar({ open: true, color: 'error', msg: err.message || 'No se pudieron seleccionar' });
        } finally {
            setSeleccionandoTodos(false);
        }
    };


    const obtenerFiltrosActivos = () => {
        const activos = [];

        // Filtros visibles (básicos)
        filtrosVisibles.forEach(f => {
            if (filtros[f.name]) {
                activos.push({
                    key: f.name,
                    label: f.label,
                    value: filtros[f.name]
                });
            }
        });

        // Helper: resolver IDs a labels desde catálogo
        const resolverArray = (ids, catalogo) => {
            if (!Array.isArray(ids) || ids.length === 0) return '';
            const nombres = ids.map(id => {
                const item = catalogo.find(c => c.id === id || c.id === parseInt(id));
                return item ? item.label : `#${id}`;
            });
            return nombres.join(', ');
        };

        // Búsqueda general q
        if (filtros.q) {
            activos.push({ key: 'q', label: 'Búsqueda', value: filtros.q });
        }

        // Filtros avanzados
        const avanzados = [
            { name: 'modulo', label: 'Área', type: 'modulo' },
            { name: 'reparticionOrigen', label: 'Repartición Origen', type: 'lookup' },
            // Compartidos
            { name: 'tiposProceso', label: 'Tipos de Proceso', type: 'array' },
            { name: 'estadosProceso', label: 'Estados', type: 'array' },
            { name: 'etapasProcesales', label: 'Estado Procesal', type: 'array' },
            { name: 'objetoReclamo', label: 'Objeto Reclamo', type: 'lookup' },
            { name: 'persona', label: 'Persona/DNI', alias: 'dni' },
            { name: 'caracterPersona', label: 'Carácter', type: 'array' },
            { name: 'juzgado', label: 'Juzgado', type: 'juzgado' },
            { name: 'certificadoDesde', label: 'Certificado Desde' },
            { name: 'certificadoHasta', label: 'Certificado Hasta' },
            { name: 'reparticion', label: 'Repartición', type: 'lookup' },
            { name: 'impuesto', label: 'Impuesto', type: 'lookup' },
            { name: 'cuenta', label: 'Cuenta', type: 'simple' },
            { name: 'planPagoEstado', label: 'Estado Plan Pago', type: 'lookup' },
            { name: 'montoDesde', label: 'Monto Desde' },
            { name: 'montoHasta', label: 'Monto Hasta' },
            { name: 'tipoMovimiento', label: 'Tipo Movimiento', type: 'special' },
            { name: 'fechaAsignacionDesde', label: 'Asignación Desde' },
            { name: 'fechaAsignacionHasta', label: 'Asignación Hasta' },
            { name: 'fechaInicioDesde', label: 'Inicio Desde' },
            { name: 'fechaInicioHasta', label: 'Inicio Hasta' },
            { name: 'fechaMovDesde', label: 'Mov. Desde' },
            { name: 'fechaMovHasta', label: 'Mov. Hasta' },
            { name: 'conCartaPoder', label: 'Con Carta Poder', type: 'boolean' },
            { name: 'interesFiscal', label: 'Interés Fiscal', type: 'simple' },
            { name: 'sinJuzgados', label: 'Sin Juzgados', type: 'boolean' },
            { name: 'sinCertificados', label: 'Sin Certificados', type: 'boolean' },
            { name: 'conMovimientos', label: 'Movimientos', type: 'boolean' },
            // Resoluciones
            { name: 'resolucionNumero', label: 'Res. Nº' },
            { name: 'resolucionDescripcion', label: 'Res. Descripción' },
            { name: 'resolucionNroExpediente', label: 'Res. Nº Expediente' },
            { name: 'resolucionOrigen', label: 'Res. Origen', type: 'lookup' },
        ];

        avanzados.forEach(f => {
            if (f.alias && activos.some(a => a.key === f.alias)) {
                return;
            }

            const valor = filtros[f.name] ?? (f.alias ? filtros[f.alias] : null);

            if (valor === undefined || valor === null || valor === '' || valor === false) return;
            if (Array.isArray(valor) && valor.length === 0) return;

            let displayValue = valor;

            // Arrays: resolver nombres desde catálogo
            if (f.type === 'array' && Array.isArray(valor) && valor.length > 0) {
                if (f.name === 'tiposProceso') {
                    displayValue = resolverArray(valor, catTiposProceso) || `${valor.length} seleccionados`;
                } else if (f.name === 'estadosProceso') {
                    displayValue = resolverArray(valor, catEstadosProceso) || `${valor.length} seleccionados`;
                } else if (f.name === 'etapasProcesales') {
                    displayValue = resolverArray(valor, catEtapasProcesales) || `${valor.length} seleccionados`;
                } else if (f.name === 'caracterPersona') {
                    displayValue = resolverArray(valor, catCaracteres) || `${valor.length} seleccionados`;
                } else {
                    displayValue = `${valor.length} seleccionados`;
                }
            }
            else if (f.type === 'special' && f.name === 'tipoMovimiento' && valor) {
                const tipoMov = tiposMovimiento.find(t => String(t.id) === String(valor) || t.id === parseInt(valor));
                displayValue = tipoMov ? tipoMov.label : `Tipo #${valor}`;
            }
            else if (f.type === 'juzgado' && Array.isArray(valor) && valor.length > 0) {
                const nombres = valor.map(id => {
                    const juz = catJuzgados.find(j => j.id === id || j.id === parseInt(id));
                    return juz ? juz.label : `#${id}`;
                });
                displayValue = nombres.join(', ');
            }
            else if (f.type === 'boolean') {
                displayValue = valor === true ? 'Sí' : 'No';
            }
            else if (f.type === 'simple' && valor) {
                displayValue = valor;
            }
            else if (f.type === 'modulo' && valor) {
                const ids = Array.isArray(valor) ? valor : [valor];
                const nombres = ids.map(id => {
                    const mod = (usuario?.modulos || []).find(m => String(m.id) === String(id));
                    return mod ? toTitleCase(mod.nombre) : `ID: ${id}`;
                });
                displayValue = nombres.length > 1 ? `${nombres.length} áreas` : nombres[0];
            }
            else if (f.type === 'lookup' && valor) {
                if (f.name === 'objetoReclamo') {
                    const obj = objetosReclamo.find(o => String(o.id) === String(valor));
                    displayValue = obj ? obj.label : `ID: ${valor}`;
                } else if (f.name === 'planPagoEstado') {
                    const est = estadosPlanPago.find(e => String(e.id) === String(valor));
                    displayValue = est ? est.label : `ID: ${valor}`;
                } else if (f.name === 'reparticion' || f.name === 'reparticionOrigen') {
                    const rep = reparticiones.find(r => String(r.id) === String(valor));
                    displayValue = rep ? rep.label : `ID: ${valor}`;
                } else if (f.name === 'impuesto') {
                    const imp = impuestos.find(i => String(i.id) === String(valor));
                    displayValue = imp ? imp.label : `ID: ${valor}`;
                } else if (f.name === 'resolucionOrigen') {
                    const rep = reparticionesTodas.find(r => String(r.id) === String(valor));
                    displayValue = rep ? rep.label : `ID: ${valor}`;
                }
            }

            activos.push({
                key: f.name,
                label: f.label,
                value: displayValue
            });
        });

        return activos;
    };

    const handleEliminarFiltro = (key) => {
        // ✅ Limpiar también los alias
        const updates = { [key]: Array.isArray(filtros[key]) ? [] : '' };

        // Si es persona, limpiar también dni
        if (key === 'persona' || key === 'dni') {
            updates.persona = '';
            updates.dni = '';
        }

        // Si es caracterPersona, limpiar array
        if (key === 'caracterPersona') {
            updates.caracterPersona = [];
        }

        setFiltros(prev => ({
            ...prev,
            ...updates
        }));
    };

    const [mostrarMasFiltros, setMostrarMasFiltros] = useState(false);
    const [catCaracteres, setCatCaracteres] = useState([]);

    const filtrosVisibles = tab === 0 ? filtrosVisiblesJudicial : filtrosVisiblesAdmin;

    // Columnas dinámicas: cambian según los filtros activos y los datos
    const columns = useMemo(() => {
        const esJud = tab === 0;
        const primary = esJud ? primaryColsJudicial : primaryColsAdmin;
        const defaults = esJud ? defaultOptionalJudicial : defaultOptionalAdmin;
        const MAX_DATA = esJud ? 5 : 4; // columnas de datos (sin Ver/Eliminar)

        // Detectar columnas dinámicas desde filtros activos
        const dynamic = [];
        const addedFields = new Set();
        for (const [filterKey, colDef] of Object.entries(filterColumnMap)) {
            const v = filtros[filterKey];
            if (v === undefined || v === null || v === '' || v === false) continue;
            if (Array.isArray(v) && v.length === 0) continue;
            if (!addedFields.has(colDef.field)) {
                dynamic.push(colDef);
                addedFields.add(colDef.field);
            }
        }

        // Judicial: agregar Certif solo si algún resultado tiene certificado real
        if (esJud && resultados.some(r => r.certificado && r.certificado !== '-' && r.certificado !== '0')) {
            if (!addedFields.has('certificado')) {
                dynamic.push({ title: "Certif", field: "certificado" });
                addedFields.add('certificado');
            }
        }

        // Construir: primary + dynamic + defaults (hasta MAX_DATA)
        const dataCols = [...primary];
        for (const col of dynamic) {
            if (dataCols.length < MAX_DATA && !dataCols.some(c => c.field === col.field))
                dataCols.push(col);
        }
        for (const col of defaults) {
            if (dataCols.length < MAX_DATA && !dataCols.some(c => c.field === col.field))
                dataCols.push(col);
        }

        // Siempre agregar Estado + Ver + (opcionalmente) Eliminar
        dataCols.push({ title: "Estado", field: "estado" });
        dataCols.push({ title: "Ver", field: "ver" });
        if (permisos.delete) dataCols.push({ title: "", field: "eliminar" });

        return dataCols;
    }, [tab, filtros, permisos.delete, resultados]);
    const location = useLocation();
    const prevTabRef = useRef(tab); // compara tab real para sobrevivir StrictMode double-mount
    const autoBuscarRef = useRef(false);
    const navigate = useNavigate();

    // Modal control
    const [open, setOpen] = useState(false);
    // Por ahora forzamos tipo, podés poner un selector (judicial/administrativo)
    const [tipo, setTipo] = useState("judicial");

    const [selectTipoOpen, setSelectTipoOpen] = useState(false);

    const handleSave = async (payload) => {
        setLoading(true);
        payload.usuario = usuario?.usuario || usuario?.nombre || "Desconocido";
        // Tipo 1=judicial, 2=administrativo (convención del backend Node)
        payload.tipo = tipo === "judicial" ? 1 : 2;

        // El form envía snake_case; el backend Node espera camelCase. Convertir:
        const snakeToCamel = (obj) => {
            const out = {};
            for (const k of Object.keys(obj)) {
                // Incluye numeros para que 'autos_1_instancia' -> 'autos1Instancia'
                // (con solo [a-z] quedaba 'autos_1Instancia' y el backend no lo leia).
                const ck = k.replace(/_([a-zA-Z0-9])/g, (_, c) => c.toUpperCase());
                out[ck] = obj[k];
            }
            return out;
        };
        const body = snakeToCamel(payload);

        try {
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            const method = payload.id ? "PUT" : "POST";
            const path = payload.id ? `expedientes/${payload.id}` : 'expedientes';
            const res = await fetch(nodeApiPath(path), {
                method,
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            const json = await res.json();
            if (!json.ok) {
                setSnackbar({ open: true, msg: json.mensaje || 'Error al guardar expediente', color: "error" });
                setLoading(false);
                setOpen(false);
                return;
            }
            const id = json.expediente?.id || payload.id;
            if (!id) {
                setSnackbar({ open: true, msg: "No se recibió el ID del nuevo expediente.", color: "error" });
                setLoading(false);
                setOpen(false);
                return;
            }
            setLoading(false);
            setOpen(false);
            setSnackbar({ open: true, msg: "Expediente guardado.", color: "success" });
            navigate(`/expediente/${tipo}/${id}`);
        } catch (err) {
            setLoading(false);
            setOpen(false);
            setSnackbar({ open: true, msg: err.message, color: "error" });
        }
    };

    // Cargar permisos del usuario
    useEffect(() => {
        (async () => {
            try {
                const res = await permisosUsuarioJWT();
                const perms = res?.permisos || {};
                // Buscar permiso: exacto por nombre de función, o parcial si contiene "EXPEDIENTE"
                const chk = (accion) => {
                    const A = (accion || '').toUpperCase();
                    for (const mod in perms) {
                        for (const func in perms[mod]) {
                            const upper = func.toUpperCase();
                            if (upper.includes('EXPEDIENTE') || upper === 'ACCESO AL MÓDULO') {
                                if (perms[mod][func]?.[A] === true) return true;
                            }
                        }
                    }
                    return false;
                };
                setPermisos({
                    insert: chk('INSERT'),
                    update: chk('UPDATE'),
                    delete: chk('DELETE'),
                });
            } catch (e) {
                console.error('Error al cargar permisos:', e);
            }
        })();
    }, []);

    useEffect(() => {
        const cargarTiposMovimiento = async () => {
            try {
                const res = await getCombosJWT('tipo_movimientos');
                if (res.ok) {
                    setTiposMovimiento((res.items || []).filter(i => i.label && i.label.trim()));
                }
            } catch (error) {
                console.error('Error al cargar tipos de movimiento:', error);
            }
        };
        cargarTiposMovimiento();
    }, []);

    useEffect(() => {
        const cargarCatalogos = async () => {
            try {
                const [resObjetos, resEstados, resReparticiones, resImpuestos,
                       resTiposProceso, resEstadosProceso, resEtapas, resJuzgados, resCaracteres, resRepTodas, resModulos] = await Promise.all([
                    getCombosJWT('objetos_reclamo'),
                    getCombosJWT('plan_pago_estados'),
                    getCombosJWT('reparticiones'),
                    getCombosJWT('impuestos'),
                    getCombosJWT('tipos_proceso'),
                    getCombosJWT('estados_proceso'),
                    getCombosJWT('etapas_proceso'),
                    getCombosJWT('juzgados'),
                    getCombosJWT('caracteres'),
                    getCombosJWT('reparticiones_todas', '', 500),
                    getCombosJWT('modulos'),
                ]);

                // Filtrar items sin label (registros vacíos de la BD)
                const clean = (arr) => (arr || []).filter(i => i.label && i.label.trim());
                if (resObjetos.ok) setObjetosReclamo(clean(resObjetos.items));
                if (resEstados.ok) setEstadosPlanPago(clean(resEstados.items));
                if (resReparticiones.ok) setReparticiones(clean(resReparticiones.items));
                if (resImpuestos.ok) setImpuestos(clean(resImpuestos.items));
                if (resTiposProceso.ok) setCatTiposProceso(clean(resTiposProceso.items));
                if (resEstadosProceso.ok) setCatEstadosProceso(clean(resEstadosProceso.items));
                if (resEtapas.ok) setCatEtapasProcesales(clean(resEtapas.items));
                if (resJuzgados.ok) setCatJuzgados(clean(resJuzgados.items));
                if (resCaracteres?.ok) setCatCaracteres(clean(resCaracteres.items));
                if (resRepTodas?.ok) setReparticionesTodas(clean(resRepTodas.items));
                if (resModulos?.ok) setModulosGlobales(clean(resModulos.items));
            } catch (error) {
                console.error('Error al cargar catálogos:', error);
            }
        };
        cargarCatalogos();
    }, []);

    // Detectar búsqueda desde el header (parámetro ?buscar=...)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const buscar = searchParams.get('buscar');

        if (buscar) {
            // Usar la búsqueda universal q
            setFiltros({ q: buscar });
            autoBuscarRef.current = true;

            // Limpiar el parámetro de la URL
            navigate(location.pathname, { replace: true });
        }
        // eslint-disable-next-line
    }, [location.search, tab]);

    // Fallback: si sessionStorage tiene filtros pero no pudo guardar resultados (quota), re-fetch
    useEffect(() => {
        if (!isEmptyFiltros(filtros) && resultados.length === 0) {
            handleBuscar(filtros);
        }
        // eslint-disable-next-line
    }, []);

    // Cambio manual de pestaña: limpiar filtros y sessionStorage
    // Usa prevTabRef en vez de flag booleano para sobrevivir StrictMode double-mount
    useEffect(() => {
        if (prevTabRef.current === tab) return; // mismo tab → mount inicial o StrictMode re-mount
        prevTabRef.current = tab;
        setFiltros(getInitialFiltros(tab));
        setResultados([]);
        setTotalResultados(0);
        setPage(0);
        setMostrarMasFiltros(false);
        sessionStorage.removeItem(SS_FILTROS_KEY);
        sessionStorage.removeItem(SS_TAB_KEY);
        sessionStorage.removeItem('sigif_home_resultados');
        sessionStorage.removeItem('sigif_home_total');
        // eslint-disable-next-line
    }, [tab]);

    // (removed: autobúsqueda de restore — ya no es necesario con state initializers)

    // No auto-cargar al entrar - el usuario busca manualmente para evitar queries innecesarias

    // Buscar expedientes
    // REEMPLAZAR handleBuscar:

    // El paginado es del SERVIDOR: `resultados` tiene solo la página visible y
    // `totalResultados` el total real de coincidencias. Antes se traían 100 (búsqueda
    // simple) o 500 (avanzada) y se paginaba en el cliente sobre eso, así que el
    // contador mostraba el tope y no había forma de llegar al resto.
    const handleBuscar = useCallback(async (overrideFiltros, opciones = {}) => {
        // Si recibe un evento de React (onClick/onSubmit), ignorarlo
        const f = (overrideFiltros && !overrideFiltros.nativeEvent && typeof overrideFiltros === 'object')
            ? overrideFiltros
            : filtros;
        const paginaPedida = opciones.pagina ?? 0;
        const porPagina = opciones.porPagina ?? rowsPerPage;
        // El orden puede venir explícito (al tocar un encabezado) o del estado actual.
        const ordenCampo = opciones.orderBy !== undefined ? opciones.orderBy : orderBy;
        const ordenDir = opciones.order !== undefined ? opciones.order : order;
        setLoading(true);

        // Guardar en sessionStorage para persistencia entre navegaciones
        sessionStorage.setItem(SS_TAB_KEY, String(tab));
        sessionStorage.setItem(SS_FILTROS_KEY, JSON.stringify(f));

        // Detectar si hay filtros avanzados (que solo busquedaAvanzada maneja)
        const tieneAvanzados = tieneFiltrosAvanzados(f);

        const tipoExp = tab === 0 ? 1 : 2;

        try {
            let data;
            if (tieneAvanzados) {
                // Usar búsqueda avanzada (POST) para filtros complejos
                data = await busquedaAvanzadaJWT(f, tipoExp, porPagina, paginaPedida * porPagina, false, ordenCampo, ordenDir);
            } else {
                // Usar búsqueda unificada (GET) para filtros simples
                data = await expedientesBusquedaUnificadaJWT({
                    tipo: tipoExp, ...f, limit: porPagina, offset: paginaPedida * porPagina,
                    orderBy: ordenCampo || undefined, order: ordenDir || undefined,
                });
            }

            if (!data.ok) {
                throw new Error(data.mensaje || 'Error en la búsqueda');
            }

            // Mapear resultados al formato de la tabla
            const fixed = mapearFilas(data.expedientes || []);

            setResultados(fixed);
            setPage(paginaPedida);
            // `total` es el total real de coincidencias, no la cantidad de esta página.
            // Si el backend no lo mandara, caer en la cantidad traída antes que en 0.
            setTotalResultados(typeof data.total === 'number' ? data.total : fixed.length);
            // Guardar resultados en sessionStorage para restauración instantánea
            try {
                sessionStorage.setItem('sigif_home_resultados', JSON.stringify(fixed));
                sessionStorage.setItem('sigif_home_total', String(typeof data.total === 'number' ? data.total : fixed.length));
            } catch { /* quota */ }
        } catch (err) {
            console.error('Error en búsqueda:', err);

            if (err.message === "Token expirado") {
                setSnackbar({
                    open: true,
                    msg: 'Tu sesión ha expirado. Redirigiendo...',
                    color: 'error'
                });
                setTimeout(() => window.location.href = '/login', 2000);
            } else {
                setSnackbar({
                    open: true,
                    msg: err.message || "Error al buscar expedientes",
                    color: "error"
                });
            }
        } finally {
            setLoading(false);
        }
    }, [tab, filtros, rowsPerPage, orderBy, order]);


    // --- Autobúsqueda al restaurar filtros (y al deshacer) ---
    useEffect(() => {
        if (autoBuscarRef.current && Object.values(filtros).some(x => x)) {
            handleBuscar();
            autoBuscarRef.current = false;
        }
    }, [filtros, handleBuscar]);

    // Escuchar tecla ESC para limpiar filtros
    useEffect(() => {
        const handleEsc = (e) => {
            // Si es ESC y no está abierto un modal/dialog
            if (e.key === "Escape") {
                handleLimpiar();
                setMostrarMasFiltros(false);
            }
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
        // eslint-disable-next-line
    }, [filtros, tab]);


    // El orden lo resuelve el SERVIDOR (ver ORDEN_EXPEDIENTES en el backend). Ordenar
    // dispara una búsqueda nueva y vuelve a la página 1: con paginado de servidor,
    // reordenar solo las filas cargadas ordenaría 25 de 765.039 y parecería un bug.
    const handleSort = col => {
        const nuevoOrder = orderBy === col && order === "asc" ? "desc" : "asc";
        setOrderBy(col);
        setOrder(nuevoOrder);
        handleBuscar(filtros, { pagina: 0, orderBy: col, order: nuevoOrder });
    };

    // `resultados` ya viene de la página que devolvió el servidor, YA ORDENADA por el
    // ORDER BY del backend. Acá no se corta ni se reordena nada: hacerlo en el cliente
    // reordenaría solo las filas visibles y contradiría al servidor.
    const getSortedRows = () => resultados;

    // Limpiar filtros
    const handleLimpiar = () => {
        setLastFiltros(filtros);
        setLastResultados(resultados);
        setLastTotal(totalResultados);

        setFiltros(getInitialFiltros(tab));
        setResultados([]);
        setTotalResultados(0);
        setPage(0);
        setExpedientesSeleccionados([]);
        sessionStorage.removeItem(SS_FILTROS_KEY);
        sessionStorage.removeItem(SS_TAB_KEY);
        sessionStorage.removeItem('sigif_home_resultados');
        sessionStorage.removeItem('sigif_home_total');

        setSnackbar({
            open: true,
            msg: "Filtros limpiados",
            undo: true,
            color: "warning"
        });

        if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
        snackbarTimer.current = setTimeout(
            () => setSnackbar(s => ({ ...s, open: false })),
            3500
        );
    };

    // Deshacer limpiar filtros
    const handleUndo = () => {
        if (lastFiltros) {
            autoBuscarRef.current = true;
            setFiltros(lastFiltros);
            setResultados(lastResultados || []);
            setTotalResultados(lastTotal || (lastResultados || []).length);
            setSnackbar({
                open: true,
                msg: "Filtros restaurados",
                undo: false,
                color: "info"
            });
        }
    };

    const handleSnackbarClose = (e, reason) => {
        if (reason === "clickaway") return;
        setSnackbar(s => ({ ...s, open: false }));
    };

    // Eliminar expediente
    const handleEliminar = async () => {
        if (!deleteDlg.id) return;
        try {
            const res = await expedienteEliminarJWT(deleteDlg.id);
            if (res.ok) {
                setResultados(prev => prev.filter(r => r.id !== deleteDlg.id));
                setTotalResultados(prev => Math.max(0, prev - 1));
                setSnackbar({ open: true, msg: "Expediente eliminado", color: "success" });
            } else {
                setSnackbar({ open: true, msg: res.mensaje || "Error al eliminar", color: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, msg: err.message || "Error al eliminar", color: "error" });
        } finally {
            setDeleteDlg({ open: false, id: null, label: '' });
        }
    };

    /**
     * Trae el resultado COMPLETO de la búsqueda actual para exportar.
     * Sin esto el export se llevaría solo la página visible, que con paginado de
     * servidor son 25 filas. Si el total supera el techo del backend (5000 por
     * request) se exporta hasta ahí y se avisa: es preferible a mentir con un
     * archivo incompleto que parece completo.
     */
    const traerTodoParaExportar = async () => {
        if (totalResultados <= resultados.length) return resultados;

        const TECHO = 5000;
        const aTraer = Math.min(totalResultados, TECHO);
        const tipoExp = tab === 0 ? 1 : 2;

        try {
            const data = tieneFiltrosAvanzados(filtros)
                ? await busquedaAvanzadaJWT(filtros, tipoExp, aTraer, 0)
                : await expedientesBusquedaUnificadaJWT({ tipo: tipoExp, ...filtros, limit: aTraer, offset: 0 });

            if (!data.ok) throw new Error(data.mensaje || 'Error al traer los datos');
            const filas = mapearFilas(data.expedientes || []);

            if (totalResultados > TECHO) {
                setSnackbar({
                    open: true,
                    color: 'warning',
                    msg: `Se exportan los primeros ${TECHO.toLocaleString('es-AR')} de ${totalResultados.toLocaleString('es-AR')}. Afiná los filtros para exportar menos.`,
                });
            }
            return filas;
        } catch (err) {
            setSnackbar({ open: true, color: 'error', msg: err.message || 'No se pudo traer el resultado completo' });
            return resultados;   // al menos exportar lo que hay en pantalla
        }
    };

    const exportToExcel = () => {
        if (resultados.length === 0) return;
        setExportando(true);
        traerTodoParaExportar().then(filas => {
            // Exportar solo las columnas visibles con nombres legibles
            const colsExport = columns.filter(c => c.field !== 'ver' && c.field !== 'eliminar');
            const data = filas.map(row => {
                const obj = {};
                colsExport.forEach(c => { obj[c.title] = row[c.field] || '-'; });
                return obj;
            });
            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Expedientes');
            XLSX.writeFile(workbook, `expedientes_${new Date().toISOString().slice(0, 10)}.xlsx`);
        }).finally(() => setExportando(false));
    };

    const exportToPDF = () => {
        if (resultados.length === 0) return;
        setExportando(true);
        traerTodoParaExportar()
            .then(filas => generarPDF(filas))
            .finally(() => setExportando(false));
    };

    // El parámetro se llama `resultados` a propósito: tapa al estado del mismo
    // nombre para que todo el armado del PDF de abajo siga funcionando igual,
    // pero sobre el resultado completo en vez de la página visible.
    const generarPDF = (resultados) => {

        const tipoExp = tab === 0 ? 'Judicial' : 'Administrativo';
        const colsExport = columns.filter(c => c.field !== 'ver' && c.field !== 'eliminar');
        const filtrosActivos = obtenerFiltrosActivos();

        getBase64FromImage(logoSIGIF, (logoBase64) => {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

            // Header
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(23, 30, 60);
            doc.text('Fiscalía de Estado - San Juan', 40, 50);

            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(31, 75, 130);
            doc.text(`Listado de Expedientes ${tipoExp}`, 40, 70);

            doc.addImage(logoBase64, 'PNG', doc.internal.pageSize.getWidth() - 175, 20, 150, 65);

            // Filtros activos
            let startY = 95;
            if (filtrosActivos.length > 0) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(100);
                doc.text('Filtros aplicados:', 40, startY);
                doc.setFont('helvetica', 'normal');
                const filtrosText = filtrosActivos.map(f => `${f.label}: ${f.value}`).join('  |  ');
                doc.text(filtrosText, 140, startY);
                startY += 18;
            }

            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(`${resultados.length} expedientes - Generado el ${new Date().toLocaleDateString('es-AR')}`, 40, startY);
            startY += 15;

            // Table
            autoTable(doc, {
                startY: startY,
                head: [colsExport.map(c => c.title)],
                body: resultados.map(row => colsExport.map(c => row[c.field] || '-')),
                styles: {
                    font: 'helvetica', fontSize: 8, cellPadding: 4,
                    overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5
                },
                headStyles: { fillColor: [23, 105, 170], textColor: 255, fontSize: 9, fontStyle: 'bold' },
                bodyStyles: { textColor: 30 },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 30, right: 30 },
            });

            // Footer with page numbers
            const pages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pages; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    `Generado el ${new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })} - Página ${i} de ${pages}`,
                    40, doc.internal.pageSize.getHeight() - 20
                );
            }

            const pdfUrl = doc.output('bloburl');
            window.open(pdfUrl, '_blank');
        });
    };

    const esJudicial = tab === 0;
    const tipoExp = esJudicial ? 1 : 2;
    const hayFiltrosActivos = obtenerFiltrosActivos().length > 0;

    // Helper para cambios en el panel expandible
    const handleFiltroPanel = (campo, valor) => {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    };

    // Contar filtros avanzados (los del panel)
    const contarFiltrosPanel = () => {
        const camposPanel = [
            'autos', 'caratula', 'certificado', 'nro_carta_poder',
            'nro_expediente', 'extracto', 'iniciador', 'reparticionOrigen',
            'fechaAsignacionDesde', 'fechaAsignacionHasta', 'fechaInicioDesde', 'fechaInicioHasta',
            'montoDesde', 'montoHasta', 'etapasProcesales', 'objetoReclamo',
            'persona', 'dni', 'idPersonal', 'caracterPersona',
            'sinCertificados', 'certificadoDesde', 'certificadoHasta', 'cuenta', 'reparticion', 'impuesto', 'planPagoEstado',
            'sinJuzgados', 'conMovimientos', 'tipoMovimiento', 'fechaMovDesde', 'fechaMovHasta', 'textoMovimiento',
            'conCartaPoder', 'nro_carta_poder', 'interesFiscal',
        ];
        return camposPanel.filter(k => {
            const v = filtros[k];
            if (v === undefined || v === null || v === '' || v === false) return false;
            if (Array.isArray(v)) return v.length > 0;
            return true;
        }).length;
    };

    return (
        <Box p={2}>
            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={snackbar.undo ? 3500 : 2000}
                onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbar.color}
                    action={snackbar.undo ? (
                        <Button color="inherit" size="small" startIcon={<Undo />} onClick={handleUndo}>Deshacer</Button>
                    ) : undefined}>
                    {snackbar.msg}
                </MuiAlert>
            </Snackbar>

            {/* Header: Breadcrumb + Nuevo Expediente */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ExpedientesHomeBreadcrumb tab={tab} />
                {permisos.insert && (
                    <Button variant="contained" color="success" startIcon={<AddIcon />}
                        onClick={() => setSelectTipoOpen(true)} size="medium"
                        sx={{ borderRadius: '4px', fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' }}>
                        Nuevo expediente
                    </Button>
                )}
            </Box>

            {/* Card principal: Tabs + Search + Dropdowns */}
            <Paper elevation={0} sx={{ mb: 2, borderRadius: '4px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                {/* Tabs integrados */}
                <Tabs value={tab} onChange={(e, v) => setTab(v)} centered
                    sx={{ '& .MuiTabs-indicator': { height: 3 } }}>
                    <Tab label="JUDICIAL" sx={{ letterSpacing: 0.5 }} />
                    <Tab label="ADMINISTRATIVO" sx={{ letterSpacing: 0.5 }} />
                </Tabs>

                <Box p={2.5} display="flex" flexDirection="column" alignItems="center">
                    {/* Search Bar — centrado y acotado */}
                    <form onSubmit={e => { e.preventDefault(); handleBuscar(); }} style={{ width: '100%', maxWidth: 650 }}>
                        <Box display="flex" gap={1.5} alignItems="center">
                            <TextField
                                fullWidth size="small"
                                placeholder={esJudicial
                                    ? "Buscar por autos, carátula, certificado..."
                                    : "Buscar por expediente, extracto, iniciador..."
                                }
                                value={filtros.q || ''}
                                onChange={e => setFiltros(prev => ({ ...prev, q: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); handleLimpiar(); } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                                        ),
                                    },
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                            />
                            <Button variant="contained" type="submit" disabled={loading}
                                startIcon={<SearchIcon />}
                                sx={{ borderRadius: '4px', px: 3, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {loading ? "Buscando..." : "Buscar"}
                            </Button>
                        </Box>
                    </form>

                {/* Quick Dropdowns */}
                <Box display="flex" gap={1.5} mt={2} flexWrap="wrap" alignItems="center" justifyContent="center">
                    {/* Estado — Autocomplete multiple con búsqueda */}
                    <Autocomplete
                        multiple size="small" sx={{ minWidth: 200 }}
                        slotProps={{ popper: { sx: { width: 'fit-content !important', minWidth: 300 } } }}
                        options={catEstadosProceso}
                        getOptionLabel={o => toTitleCase(o.label) || ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={(filtros.estadosProceso || []).map(id => catEstadosProceso.find(e => e.id === id)).filter(Boolean)}
                        onChange={(e, val) => handleFiltroPanel('estadosProceso', val.map(v => v.id))}
                        disableCloseOnSelect
                        renderOption={(props, option, { selected, inputValue, index }) => {
                            const { key, ...rest } = props;
                            return (
                                <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>
                                    <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                                    {highlightMatch(toTitleCase(option.label), inputValue)}
                                </li>
                            );
                        }}
                        renderInput={params => <TextField {...params} label="Estado" placeholder="Buscar..." />}
                        limitTags={1}
                    />

                    {/* Tipo Proceso — Autocomplete multiple con búsqueda */}
                    <Autocomplete
                        multiple size="small" sx={{ minWidth: 220 }}
                        slotProps={{ popper: { sx: { width: 'fit-content !important', minWidth: 320 } } }}
                        options={catTiposProceso}
                        getOptionLabel={o => toTitleCase(o.label) || ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={(filtros.tiposProceso || []).map(id => catTiposProceso.find(t => t.id === id)).filter(Boolean)}
                        onChange={(e, val) => handleFiltroPanel('tiposProceso', val.map(v => v.id))}
                        disableCloseOnSelect
                        renderOption={(props, option, { selected, inputValue, index }) => {
                            const { key, ...rest } = props;
                            return (
                                <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>
                                    <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                                    {highlightMatch(toTitleCase(option.label), inputValue)}
                                </li>
                            );
                        }}
                        renderInput={params => <TextField {...params} label="Tipo Proceso" placeholder="Buscar..." />}
                        limitTags={1}
                    />

                    {/* Área (Módulo) — multi-select. Filtra por área de la institución
                        (Inmuebles, Ejecuciones, etc.). Matchea tanto el área origen como
                        las áreas conjuntas activas del expediente.
                        Toggle "Excluir" invierte la lógica: trae todo MENOS las áreas elegidas. */}
                    {(() => {
                        // Para que el filtro aparezca a todos: si el usuario no tiene
                        // módulos cargados o tiene solo 1, usamos los módulos del
                        // catálogo global (todos los de la institución). El filtro
                        // termina siendo igual de útil — los exp se filtran después.
                        const userMods = (usuario?.modulos || [])
                            .filter(m => m.clave === 'expediente_judicial')
                            .reduce((acc, m) => {
                                if (!acc.some(x => x.id === m.id)) acc.push({ id: m.id, label: m.nombre });
                                return acc;
                            }, []);
                        const modulosOpts = userMods.length > 1 ? userMods : modulosGlobales;
                        if (modulosOpts.length === 0) return null;
                        const selectedIds = Array.isArray(filtros.modulo)
                            ? filtros.modulo
                            : (filtros.modulo ? String(filtros.modulo).split(',').map(s => parseInt(s)).filter(Boolean) : []);
                        const selectedOpts = selectedIds.map(id => modulosOpts.find(o => o.id === id)).filter(Boolean);
                        const excluir = !!filtros.moduloExcluir;
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Autocomplete
                                    multiple size="small" sx={{ minWidth: 220 }}
                                    slotProps={{ popper: { sx: { width: 'fit-content !important', minWidth: 360 } } }}
                                    options={modulosOpts}
                                    getOptionLabel={o => toTitleCase(o.label) || ''}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    value={selectedOpts}
                                    onChange={(e, vals) => handleFiltroPanel('modulo', vals.length ? vals.map(v => v.id) : null)}
                                    disableCloseOnSelect
                                    renderOption={(props, option, { selected, inputValue, index }) => {
                                        const { key, ...rest } = props;
                                        return (
                                            <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>
                                                <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                                                {highlightMatch(toTitleCase(option.label), inputValue)}
                                            </li>
                                        );
                                    }}
                                    renderInput={params => (
                                        <TextField {...params}
                                            label={excluir ? 'Área (excluir)' : 'Área'}
                                            placeholder="Todas" />
                                    )}
                                    limitTags={1}
                                />
                                <Tooltip title={excluir ? 'Cambiar a Incluir (trae solo estas áreas)' : 'Cambiar a Excluir (trae todo menos estas áreas)'}>
                                    <span>
                                        <Button
                                            size="small"
                                            variant={excluir ? 'contained' : 'outlined'}
                                            color={excluir ? 'error' : 'primary'}
                                            onClick={() => handleFiltroPanel('moduloExcluir', !excluir)}
                                            disabled={selectedIds.length === 0}
                                            sx={{ minWidth: 78, px: 1, py: 0.5, fontSize: '0.7rem', textTransform: 'none', whiteSpace: 'nowrap' }}
                                        >
                                            {excluir ? 'Excluir' : 'Incluir'}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        );
                    })()}

                    {/* Juzgado (Judicial) o Repartición Origen (Admin) */}
                    {esJudicial ? (
                        <Autocomplete
                            multiple size="small" sx={{ minWidth: 280 }}
                            slotProps={{ popper: { sx: { width: 'fit-content !important', minWidth: 450 } } }}
                            options={[...catJuzgados].sort((a, b) => {
                                const fA = (a.label || '').split(' | ')[1] || '';
                                const fB = (b.label || '').split(' | ')[1] || '';
                                return fA.localeCompare(fB) || (a.label || '').localeCompare(b.label || '');
                            })}
                            groupBy={(option) => toTitleCase((option.label || '').split(' | ')[1] || 'Sin Fuero')}
                            getOptionLabel={o => {
                                const parts = (o.label || '').split(' | ');
                                const nombre = toTitleCase(parts[0] || '');
                                const fuero = toTitleCase(parts[1] || '');
                                const jurisd = toTitleCase(parts[2] || '');
                                return [nombre, fuero, jurisd].filter(Boolean).join(' - ');
                            }}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={(filtros.juzgado || []).map(id => catJuzgados.find(j => j.id === id)).filter(Boolean)}
                            onChange={(e, val) => handleFiltroPanel('juzgado', val.map(v => v.id))}
                            disableCloseOnSelect
                            renderOption={(props, option, { selected, inputValue, index }) => {
                                const { key, ...rest } = props;
                                const parts = (option.label || '').split(' | ');
                                const nombre = toTitleCase(parts[0] || '');
                                const jurisdiccion = toTitleCase(parts[2] || '');
                                return (
                                    <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), alignItems: 'flex-start', padding: '6px 12px' }}>
                                        <Checkbox checked={selected} size="small" sx={{ mt: -0.3, mr: 0.5 }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>{highlightMatch(nombre, inputValue)}</Typography>
                                            {jurisdiccion && <Typography variant="caption" color="text.secondary">{jurisdiccion}</Typography>}
                                        </Box>
                                    </li>
                                );
                            }}
                            renderGroup={(params) => (
                                <li key={params.key}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', px: 1.5, py: 0.5, bgcolor: '#e3f2fd', display: 'block', position: 'sticky', top: -8, zIndex: 1 }}>
                                        {params.group}
                                    </Typography>
                                    <ul style={{ padding: 0 }}>{params.children}</ul>
                                </li>
                            )}
                            renderInput={params => <TextField {...params} label="Juzgado" placeholder="Buscar..." />}
                            limitTags={1}
                        />
                    ) : (
                        <Autocomplete
                            size="small" sx={{ minWidth: 280 }}
                            slotProps={{ popper: { sx: { width: 'fit-content !important', minWidth: 400 } } }}
                            options={reparticiones}
                            getOptionLabel={o => toTitleCase(o.label) || ''}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={reparticiones.find(r => r.id === filtros.reparticionOrigen) || null}
                            onChange={(e, val) => handleFiltroPanel('reparticionOrigen', val?.id || null)}
                            renderOption={(props, option, { inputValue, index }) => {
                                const { key, ...rest } = props;
                                return (
                                    <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>
                                        {highlightMatch(toTitleCase(option.label), inputValue)}
                                    </li>
                                );
                            }}
                            renderInput={params => <TextField {...params} label="Repartición Origen" placeholder="Buscar..." />}
                        />
                    )}

                    {/* Botón Más Filtros */}
                    <Button
                        startIcon={<TuneIcon />}
                        onClick={() => setMostrarMasFiltros(v => !v)}
                        variant={mostrarMasFiltros ? "contained" : "outlined"}
                        color={contarFiltrosPanel() > 0 ? "primary" : "inherit"}
                        sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        Filtros {contarFiltrosPanel() > 0 ? `(${contarFiltrosPanel()})` : ''}
                    </Button>

                    {/* Limpiar */}
                    {hayFiltrosActivos && (
                        <Button startIcon={<ClearIcon />} onClick={handleLimpiar} size="small"
                            sx={{ textTransform: 'none', borderRadius: '4px' }}>
                            Limpiar
                        </Button>
                    )}
                </Box>
                </Box>
            </Paper>

            {/* Panel Expandible de Filtros */}
            <Collapse in={mostrarMasFiltros} timeout={300}>
                <Paper elevation={0} sx={{ p: 3, mb: 2, borderRadius: '4px', border: '1px solid', borderColor: 'divider' }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBuscar(); } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="subtitle1" fontWeight={700} color="primary">Filtros Detallados</Typography>
                        <IconButton size="small" onClick={() => setMostrarMasFiltros(false)}><ClearIcon fontSize="small" /></IconButton>
                    </Box>

                    <Box display="flex" flexWrap="wrap" gap={3}>
                        {/* SECCIÓN: Búsqueda por campo */}
                        <Box sx={{ flex: '1 1 320px', minWidth: 280 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom display="block">
                                BÚSQUEDA POR CAMPO
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                {esJudicial ? (
                                    <>
                                        <TextField size="small" label="Autos" value={filtros.autos || ''} onChange={e => handleFiltroPanel('autos', e.target.value)} />
                                        <TextField size="small" label="Carátula" value={filtros.caratula || ''} onChange={e => handleFiltroPanel('caratula', e.target.value)} />
                                        <TextField size="small" label="Nº Certificado" value={filtros.certificado || ''} onChange={e => handleFiltroPanel('certificado', e.target.value)} />
                                        <TextField size="small" label="Nº Carta Poder" value={filtros.nro_carta_poder || ''} onChange={e => handleFiltroPanel('nro_carta_poder', e.target.value)} />
                                    </>
                                ) : (
                                    <>
                                        <TextField size="small" label="Nº Expediente" value={filtros.nro_expediente || ''} onChange={e => handleFiltroPanel('nro_expediente', e.target.value)} />
                                        <TextField size="small" label="Iniciador" value={filtros.iniciador || ''} onChange={e => handleFiltroPanel('iniciador', e.target.value)} />
                                        <TextField size="small" label="Extracto" value={filtros.extracto || ''} onChange={e => handleFiltroPanel('extracto', e.target.value)} />
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* SECCIÓN: Fechas */}
                        <Box sx={{ flex: '1 1 320px', minWidth: 280 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom display="block">
                                FECHAS
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <Box display="flex" gap={1}>
                                    <TextField size="small" label="Asignación Desde" type="date" fullWidth
                                        value={filtros.fechaAsignacionDesde || ''} onChange={e => handleFiltroPanel('fechaAsignacionDesde', e.target.value)}
                                        InputLabelProps={{ shrink: true }} />
                                    <TextField size="small" label="Hasta" type="date" fullWidth
                                        value={filtros.fechaAsignacionHasta || ''} onChange={e => handleFiltroPanel('fechaAsignacionHasta', e.target.value)}
                                        InputLabelProps={{ shrink: true }} />
                                </Box>
                                <Box display="flex" gap={1}>
                                    <TextField size="small" label="Inicio Desde" type="date" fullWidth
                                        value={filtros.fechaInicioDesde || ''} onChange={e => handleFiltroPanel('fechaInicioDesde', e.target.value)}
                                        InputLabelProps={{ shrink: true }} />
                                    <TextField size="small" label="Hasta" type="date" fullWidth
                                        value={filtros.fechaInicioHasta || ''} onChange={e => handleFiltroPanel('fechaInicioHasta', e.target.value)}
                                        InputLabelProps={{ shrink: true }} />
                                </Box>
                            </Box>

                            {esJudicial && (
                                <>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" mt={2} gutterBottom display="block">
                                        DATOS ADICIONALES
                                    </Typography>
                                    <Box display="flex" gap={1} mb={1}>
                                        <TextField size="small" label="Monto Desde" type="number" fullWidth
                                            value={filtros.montoDesde || ''} onChange={e => handleFiltroPanel('montoDesde', e.target.value)} />
                                        <TextField size="small" label="Monto Hasta" type="number" fullWidth
                                            value={filtros.montoHasta || ''} onChange={e => handleFiltroPanel('montoHasta', e.target.value)} />
                                    </Box>
                                    <Autocomplete
                                        multiple size="small" fullWidth sx={{ mb: 1 }}
                                        options={catEtapasProcesales}
                                        getOptionLabel={o => toTitleCase(o.label) || ''}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        value={(filtros.etapasProcesales || []).map(id => catEtapasProcesales.find(e => e.id === id)).filter(Boolean)}
                                        onChange={(e, val) => handleFiltroPanel('etapasProcesales', val.map(v => v.id))}
                                        disableCloseOnSelect
                                        renderOption={(props, option, { selected, inputValue, index }) => {
                                            const { key, ...rest } = props;
                                            return (
                                                <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>
                                                    <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                                                    {highlightMatch(toTitleCase(option.label), inputValue)}
                                                </li>
                                            );
                                        }}
                                        renderInput={params => <TextField {...params} label="Estado Procesal" placeholder="Buscar..." />}
                                        limitTags={2}
                                    />
                                </>
                            )}

                            <Autocomplete size="small" fullWidth sx={{ mt: 1 }}
                                options={objetosReclamo}
                                getOptionLabel={o => toTitleCase(o.label) || ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={objetosReclamo.find(o => o.id === filtros.objetoReclamo) || null}
                                onChange={(e, val) => handleFiltroPanel('objetoReclamo', val?.id || null)}
                                renderOption={(props, option, { inputValue, index }) => {
                                    const { key, ...rest } = props;
                                    return (
                                        <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>
                                            {highlightMatch(toTitleCase(option.label), inputValue)}
                                        </li>
                                    );
                                }}
                                renderInput={params => <TextField {...params} label={esJudicial ? "Objeto de Reclamo" : "Asunto (Objeto de Reclamo)"} />}
                            />
                        </Box>

                        {/* SECCIÓN: Partes */}
                        <Box sx={{ flex: '1 1 320px', minWidth: 280 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom display="block">
                                PARTES
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <BuscadorPersonas
                                    label="Persona / DNI"
                                    placeholder="Buscar por nombre o DNI..."
                                    value={filtros.persona || filtros.dni || ''}
                                    onChange={(nombre, idPersonal) => {
                                        if (idPersonal) {
                                            setFiltros(prev => ({ ...prev, idPersonal, persona: nombre, dni: undefined }));
                                        } else if (/^[\d\-\s]+$/.test(nombre)) {
                                            setFiltros(prev => ({ ...prev, dni: nombre, persona: undefined, idPersonal: undefined }));
                                        } else {
                                            setFiltros(prev => ({ ...prev, persona: nombre, dni: undefined, idPersonal: undefined }));
                                        }
                                    }}
                                />
                                <Autocomplete
                                    multiple size="small" fullWidth
                                    options={catCaracteres}
                                    getOptionLabel={o => toTitleCase(o.label) || ''}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    value={(filtros.caracterPersona || []).map(id => catCaracteres.find(c => c.id === id)).filter(Boolean)}
                                    onChange={(e, val) => handleFiltroPanel('caracterPersona', val.map(v => v.id))}
                                    disableCloseOnSelect
                                    renderOption={(props, option, { selected, inputValue, index }) => {
                                        const { key, ...rest } = props;
                                        return (
                                            <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>
                                                <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                                                {highlightMatch(toTitleCase(option.label), inputValue)}
                                            </li>
                                        );
                                    }}
                                    renderInput={params => <TextField {...params} label="Carácter" placeholder="Buscar..." />}
                                    limitTags={1}
                                />
                            </Box>

                            {/* Movimientos */}
                            <Typography variant="caption" fontWeight={600} color="text.secondary" mt={2} gutterBottom display="block">
                                MOVIMIENTOS
                            </Typography>
                            <RadioGroup row value={filtros.conMovimientos ?? ''} onChange={e => {
                                const v = e.target.value === '' ? null : e.target.value === 'true';
                                // Ya no limpiamos fechas/tipo al elegir "Sin" — el backend
                                // las usa para buscar exp que NO tuvieron movs EN ESE RANGO.
                                setFiltros(prev => ({ ...prev, conMovimientos: v }));
                            }}>
                                <FormControlLabel value="" control={<Radio size="small" />} label="Todos" />
                                <FormControlLabel value="true" control={<Radio size="small" />} label="Con" />
                                <FormControlLabel value="false" control={<Radio size="small" />} label="Sin" />
                            </RadioGroup>
                            {/* Inputs de tipo + fechas SIEMPRE visibles, sin importar Con/Sin.
                                Con "Sin" + fechas → exp que NO tuvieron movs en ese rango.
                                Con "Con" + fechas → exp con al menos un mov en ese rango. */}
                            <Box display="flex" flexDirection="column" gap={1} mt={0.5}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Tipo Movimiento</InputLabel>
                                    <Select value={filtros.tipoMovimiento || ''}
                                        onChange={e => handleFiltroPanel('tipoMovimiento', e.target.value || null)}
                                        input={<OutlinedInput label="Tipo Movimiento" />}>
                                        <MenuItem value="">Todos</MenuItem>
                                        {tiposMovimiento.filter(t => !['EXPEDIENTE ELECTRONICO', 'EXPEDIENTE ELECTRÓNICO', 'FORMULARIOS', 'GENERADOR DE CARTA PODER', 'REGISTRO CANCELADO', 'REQUERIMIENTOS'].includes((t.label || '').toUpperCase())).map((t, idx) => {
                                            const visual = (t.label || '').toUpperCase().includes('OTRO MOV') ? 'Movimiento Administrativo' : toTitleCase(t.label);
                                            return <MenuItem key={t.id} value={t.id} sx={{ ...zebraStyle(idx), py: 0.75 }}>{visual}</MenuItem>;
                                        })}
                                    </Select>
                                </FormControl>
                                <Box display="flex" gap={1}>
                                    <TextField size="small" label="Mov. Desde" type="date" fullWidth
                                        value={filtros.fechaMovDesde || ''} onChange={e => handleFiltroPanel('fechaMovDesde', e.target.value)}
                                        InputLabelProps={{ shrink: true }} />
                                    <TextField size="small" label="Mov. Hasta" type="date" fullWidth
                                        value={filtros.fechaMovHasta || ''} onChange={e => handleFiltroPanel('fechaMovHasta', e.target.value)}
                                        InputLabelProps={{ shrink: true }} />
                                </Box>
                                {/* Texto libre en la descripción del movimiento — busca exp
                                    con (o sin) un movimiento cuya descripción contenga el texto. */}
                                <TextField size="small" label="Texto en el movimiento" fullWidth
                                    placeholder="Busca en la descripción del movimiento"
                                    value={filtros.textoMovimiento || ''}
                                    onChange={e => handleFiltroPanel('textoMovimiento', e.target.value)} />
                            </Box>

                            {/* Carta Poder */}
                            <Typography variant="caption" fontWeight={600} color="text.secondary" mt={2} gutterBottom display="block">
                                CARTA PODER
                            </Typography>
                            <RadioGroup row value={filtros.conCartaPoder ?? ''} onChange={e => {
                                const v = e.target.value === '' ? null : e.target.value === 'true';
                                setFiltros(prev => ({ ...prev, conCartaPoder: v, ...(v === false ? { nro_carta_poder: '' } : {}) }));
                            }}>
                                <FormControlLabel value="" control={<Radio size="small" />} label="Todos" />
                                <FormControlLabel value="true" control={<Radio size="small" />} label="Con" />
                                <FormControlLabel value="false" control={<Radio size="small" />} label="Sin" />
                            </RadioGroup>
                            {filtros.conCartaPoder !== false && (
                                <TextField size="small" fullWidth label="Nº Carta Poder"
                                    value={filtros.nro_carta_poder || ''}
                                    onChange={e => {
                                        const val = e.target.value;
                                        setFiltros(prev => ({ ...prev, nro_carta_poder: val, ...(val ? { conCartaPoder: true } : {}) }));
                                    }}
                                    sx={{ mt: 0.5 }} />
                            )}

                            {/* Inmuebles — Interés Fiscal (sistema viejo, sept-2026). El backend
                                acota la búsqueda al módulo Inmuebles de Interés Estatal: ver
                                busquedaAvanzada. "No" incluye los expedientes sin inmuebles. */}
                            <Typography variant="caption" fontWeight={600} color="text.secondary" mt={2} gutterBottom display="block">
                                INMUEBLES — INTERÉS FISCAL
                            </Typography>
                            <RadioGroup row value={filtros.interesFiscal || ''}
                                onChange={e => handleFiltroPanel('interesFiscal', e.target.value || null)}>
                                <FormControlLabel value="" control={<Radio size="small" />} label="Indiferente" />
                                <FormControlLabel value="SI" control={<Radio size="small" />} label="Sí" />
                                <FormControlLabel value="NO" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                            {filtros.interesFiscal && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Solo expedientes del módulo Inmuebles de Interés Estatal.
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Resoluciones */}
                    <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                        <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom display="block">
                            RESOLUCIONES
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1.5} mt={1}>
                            <TextField size="small" label="Nº Resolución" value={filtros.resolucionNumero || ''} onChange={e => handleFiltroPanel('resolucionNumero', e.target.value)} sx={{ flex: '1 1 180px' }} />
                            <TextField size="small" label="Descripción" value={filtros.resolucionDescripcion || ''} onChange={e => handleFiltroPanel('resolucionDescripcion', e.target.value)} sx={{ flex: '1 1 220px' }} />
                            <TextField size="small" label="Nº Expediente (Res.)" value={filtros.resolucionNroExpediente || ''} onChange={e => handleFiltroPanel('resolucionNroExpediente', e.target.value)} sx={{ flex: '1 1 180px' }} />
                            <Autocomplete size="small" sx={{ flex: '1 1 250px' }}
                                options={reparticionesTodas}
                                getOptionLabel={o => toTitleCase(o.label) || ''}
                                value={reparticionesTodas.find(r => String(r.id) === String(filtros.resolucionOrigen)) || null}
                                onChange={(e, val) => handleFiltroPanel('resolucionOrigen', val?.id || null)}
                                renderOption={(props, option, { inputValue, index }) => {
                                    const { key, ...rest } = props;
                                    return <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>{highlightMatch(toTitleCase(option.label), inputValue)}</li>;
                                }}
                                renderInput={params => <TextField {...params} label="Origen (Repartición)" />}
                            />
                        </Box>
                    </Box>

                    {/* Certificados y Radicación (solo judicial) */}
                    {esJudicial && (
                        <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                            <Box display="flex" flexWrap="wrap" gap={3}>
                                <Box sx={{ flex: '1 1 400px', minWidth: 300 }}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom display="block">
                                        CERTIFICADOS
                                    </Typography>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={!!filtros.sinCertificados}
                                            onChange={e => handleFiltroPanel('sinCertificados', e.target.checked)} />}
                                        label="Sin certificados"
                                    />
                                    {!filtros.sinCertificados && (
                                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                            <TextField size="small" label="Cert. Desde" value={filtros.certificadoDesde || ''} onChange={e => handleFiltroPanel('certificadoDesde', e.target.value)} sx={{ flex: '1 1 120px' }} />
                                            <TextField size="small" label="Cert. Hasta" value={filtros.certificadoHasta || ''} onChange={e => handleFiltroPanel('certificadoHasta', e.target.value)} sx={{ flex: '1 1 120px' }} />
                                            <TextField size="small" label="Cuenta" value={filtros.cuenta || ''} onChange={e => handleFiltroPanel('cuenta', e.target.value)} sx={{ flex: '1 1 120px' }} />
                                            <Autocomplete size="small" sx={{ flex: '1 1 180px' }}
                                                options={reparticiones}
                                                getOptionLabel={o => toTitleCase(o.label) || ''}
                                                value={reparticiones.find(r => String(r.id) === String(filtros.reparticion)) || null}
                                                onChange={(e, val) => handleFiltroPanel('reparticion', val?.id || null)}
                                                renderOption={(props, option, { inputValue, index }) => {
                                                    const { key, ...rest } = props;
                                                    return <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>{highlightMatch(toTitleCase(option.label), inputValue)}</li>;
                                                }}
                                                renderInput={params => <TextField {...params} label="Repartición" />}
                                            />
                                            <Autocomplete size="small" sx={{ flex: '1 1 180px' }}
                                                options={impuestos}
                                                getOptionLabel={o => toTitleCase(o.label) || ''}
                                                value={impuestos.find(i => String(i.id) === String(filtros.impuesto)) || null}
                                                onChange={(e, val) => handleFiltroPanel('impuesto', val?.id || null)}
                                                renderOption={(props, option, { inputValue, index }) => {
                                                    const { key, ...rest } = props;
                                                    return <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>{highlightMatch(toTitleCase(option.label), inputValue)}</li>;
                                                }}
                                                renderInput={params => <TextField {...params} label="Impuesto" />}
                                            />
                                            <Autocomplete size="small" sx={{ flex: '1 1 180px' }}
                                                options={estadosPlanPago}
                                                getOptionLabel={o => toTitleCase(o.label) || ''}
                                                value={estadosPlanPago.find(e => String(e.id) === String(filtros.planPagoEstado)) || null}
                                                onChange={(e, val) => handleFiltroPanel('planPagoEstado', val?.id || null)}
                                                renderOption={(props, option, { inputValue, index }) => {
                                                    const { key, ...rest } = props;
                                                    return <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), padding: '6px 12px' }}>{highlightMatch(toTitleCase(option.label), inputValue)}</li>;
                                                }}
                                                renderInput={params => <TextField {...params} label="Estado Plan Pago" />}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ flex: '0 1 200px', minWidth: 150 }}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom display="block">
                                        RADICACIÓN
                                    </Typography>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={!!filtros.sinJuzgados}
                                            onChange={e => handleFiltroPanel('sinJuzgados', e.target.checked)} />}
                                        label="Sin juzgados"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Botón Buscar dentro del panel */}
                    <Box display="flex" justifyContent="flex-end" mt={3} pt={2} borderTop="1px solid" borderColor="divider">
                        <Button variant="contained" startIcon={<SearchIcon />} onClick={() => handleBuscar()}
                            disabled={loading}
                            sx={{ borderRadius: '4px', px: 4, textTransform: 'none', fontWeight: 600 }}>
                            {loading ? "Buscando..." : "Buscar"}
                        </Button>
                    </Box>
                </Paper>
            </Collapse>

            {/* Active Filter Chips */}
            {hayFiltrosActivos && (
                <Paper elevation={0} sx={{ mb: 2, p: 1.5, borderRadius: '4px', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    {obtenerFiltrosActivos().map(filtro => (
                        <Chip key={filtro.key} label={`${filtro.label}: ${filtro.value}`}
                            onDelete={() => handleEliminarFiltro(filtro.key)}
                            color="primary" size="small" />
                    ))}
                    <Button size="small" startIcon={<ClearIcon />} onClick={handleLimpiar}
                        sx={{ ml: 1, textTransform: 'none', color: 'error.main' }}>
                        Limpiar todos
                    </Button>
                </Paper>
            )}

            {/* Forbidden warning */}
            {forbidden && (
                <Box mt={2}>
                    <MuiAlert elevation={6} variant="filled" severity="warning">
                        No tenés permiso para listar/buscar expedientes. Pedí que te habiliten "BÚSQUEDA DE EXPEDIENTES".
                    </MuiAlert>
                </Box>
            )}

            {/* Results */}
            <Box mt={1}>
                <TableContainer component={Paper} style={{ borderRadius: 4 }}>
                    {/* Toolbar integrado: contador + export + lista */}
                    {(resultados.length > 0 || expedientesSeleccionados.length > 0) && (
                        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1}
                            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                <b>{totalResultados.toLocaleString('es-AR')}</b> expediente{totalResultados !== 1 ? 's' : ''} encontrado{totalResultados !== 1 ? 's' : ''}
                                {totalResultados > resultados.length && (
                                    <span style={{ marginLeft: 6 }}>
                                        (mostrando {resultados.length.toLocaleString('es-AR')})
                                    </span>
                                )}
                                {expedientesSeleccionados.length > 0 && (
                                    <span style={{ marginLeft: 12 }}>
                                        &bull; <b>{expedientesSeleccionados.length}</b> seleccionado{expedientesSeleccionados.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </Typography>
                            <Box display="flex" gap={0.5} alignItems="center">
                                {/* El checkbox de la cabecera solo alcanza la pagina visible
                                    (25 filas). Este boton trae del servidor los ids de todo
                                    lo que coincide, para poder agregarlo a una lista. */}
                                {totalResultados > resultados.length && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleSeleccionarCoincidentes}
                                        disabled={seleccionandoTodos}
                                        sx={{ mr: 1, textTransform: 'none', borderRadius: '4px' }}
                                    >
                                        {seleccionandoTodos
                                            ? 'Seleccionando...'
                                            : `Seleccionar los ${totalResultados.toLocaleString('es-AR')}`}
                                    </Button>
                                )}
                                <Tooltip title={totalResultados > resultados.length
                                    ? `Exportar a Excel los ${Math.min(totalResultados, 5000).toLocaleString('es-AR')} resultados`
                                    : 'Exportar a Excel'}>
                                    <span>
                                        <IconButton onClick={exportToExcel} color="primary" size="small" disabled={exportando}><GetApp /></IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={totalResultados > resultados.length
                                    ? `Exportar a PDF los ${Math.min(totalResultados, 5000).toLocaleString('es-AR')} resultados`
                                    : 'Exportar a PDF'}>
                                    <span>
                                        <IconButton onClick={exportToPDF} color="error" size="small" disabled={exportando}><PictureAsPdfIcon /></IconButton>
                                    </span>
                                </Tooltip>
                                {expedientesSeleccionados.length > 0 && (
                                    <Button variant="outlined" color="primary" size="small" startIcon={<PlaylistAddIcon />}
                                        onClick={() => setMostrarSelectorListas(true)} sx={{ ml: 1, textTransform: 'none', borderRadius: '4px' }}>
                                        Lista ({expedientesSeleccionados.length})
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={expedientesSeleccionados.length > 0 && expedientesSeleccionados.length < resultados.length}
                                        checked={resultados.length > 0 && expedientesSeleccionados.length === resultados.length}
                                        onChange={handleToggleTodos}
                                    />
                                </TableCell>
                                {columns.map(col => (
                                    <TableCell key={col.field} align="left">
                                        {col.field !== "ver" && col.field !== "eliminar" ? (
                                            <TableSortLabel active={orderBy === col.field}
                                                direction={orderBy === col.field ? order : "asc"}
                                                onClick={() => handleSort(col.field)}>
                                                {col.title}
                                            </TableSortLabel>
                                        ) : col.title}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableSkeleton rows={rowsPerPage} columns={columns.length} />
                            ) : (
                                <>
                                    {getSortedRows().map((row, idx) => (
                                        <TableRow hover key={row.id || idx}
                                            onClick={() => navigate(`/expediente/${esJudicial ? "judicial" : "administrativo"}/${row.id}`, { state: { tab, tipo: esJudicial ? "judicial" : "administrativo", filtros } })}
                                            sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent", cursor: "pointer", '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08) !important' } }}>
                                            <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                                <Checkbox checked={expedientesSeleccionados.includes(row.id)} onChange={() => handleToggleSeleccion(row.id)} />
                                            </TableCell>
                                            {columns.map(col =>
                                                col.field === "ver" ? (
                                                    <TableCell key={col.field} onClick={e => e.stopPropagation()}>
                                                        <Link to={`/expediente/${esJudicial ? "judicial" : "administrativo"}/${row.id}`}
                                                            state={{ tab, tipo: esJudicial ? "judicial" : "administrativo", filtros }}
                                                            style={{ textDecoration: "none" }}>
                                                            <Button size="small" color="primary">Ver</Button>
                                                        </Link>
                                                    </TableCell>
                                                ) : col.field === "eliminar" ? (
                                                    <TableCell key={col.field} align="center" style={{ width: 48 }} onClick={e => e.stopPropagation()}>
                                                        <Tooltip title="Eliminar expediente">
                                                            <IconButton size="small" color="error"
                                                                onClick={() => setDeleteDlg({ open: true, id: row.id, label: row.autos || row.nro_expediente || row.caratula || `#${row.id}` })}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                ) : (
                                                    <TableCell key={col.field}>
                                                        <Tooltip title={row[col.field] || ""} enterDelay={500}>
                                                            <span>{row[col.field] || "-"}</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    ))}
                                    {resultados.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={columns.length + 1} align="center" style={{ padding: 40 }}>
                                                <Typography variant="body1" color="text.secondary">
                                                    Ingrese los criterios de búsqueda y presione "Buscar"
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                    {/* count = total real del servidor. Cambiar de página o de tamaño
                        dispara una búsqueda nueva: los datos no están todos en memoria. */}
                    <TablePagination component="div" count={totalResultados} page={page}
                        onPageChange={(e, newPage) => handleBuscar(filtros, { pagina: newPage })}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            const nuevoTam = parseInt(e.target.value, 10);
                            setRowsPerPage(nuevoTam);
                            handleBuscar(filtros, { pagina: 0, porPagina: nuevoTam });
                        }}
                        labelRowsPerPage="Filas por página" rowsPerPageOptions={[25, 50, 100, 200]}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from.toLocaleString('es-AR')}–${to.toLocaleString('es-AR')} de ${count.toLocaleString('es-AR')}`}
                    />
                </TableContainer>
            </Box>

            {/* Dialog: Seleccionar tipo nuevo expediente */}
            <Dialog open={selectTipoOpen} onClose={() => setSelectTipoOpen(false)}
                PaperProps={{ style: { borderRadius: 4, background: "#f9f9f9", maxWidth: 360, width: "92vw", boxShadow: "0 2px 18px #0002" } }}>
                <DialogTitle style={{ textAlign: "center", fontWeight: 700, fontSize: 20, paddingBottom: 4 }}>
                    Nuevo expediente:
                </DialogTitle>
                <DialogContent style={{ padding: 24, minWidth: 220, maxWidth: 340, overflowX: "hidden", textAlign: "center" }}>
                    <Button onClick={() => { setTipo("judicial"); setOpen(true); setSelectTipoOpen(false); }}
                        color="primary" fullWidth startIcon={<GavelIcon style={{ color: "#1976d2", fontSize: 26 }} />}
                        style={{ margin: "14px 0", borderRadius: 4, fontWeight: 700, fontSize: 16, borderWidth: 2, textTransform: "uppercase", letterSpacing: 1.5 }}
                        variant="outlined" disableElevation>
                        Judicial
                    </Button>
                    <Button onClick={() => { setTipo("administrativo"); setOpen(true); setSelectTipoOpen(false); }}
                        style={{ margin: "14px 0", borderRadius: 4, fontWeight: 700, fontSize: 16, borderWidth: 2, textTransform: "uppercase", letterSpacing: 1.5, borderColor: "#d32f2f", color: "#d32f2f" }}
                        fullWidth startIcon={<DescriptionIcon style={{ color: "#d32f2f", fontSize: 26 }} />}
                        variant="outlined" disableElevation>
                        Administrativo
                    </Button>
                </DialogContent>
            </Dialog>

            {/* Modal de Alta */}
            <ExpedienteForm open={open} onClose={() => setOpen(false)} onSave={handleSave} tipo={tipo} />

            {/* Dialog de confirmación de eliminación */}
            <Dialog open={deleteDlg.open} onClose={() => setDeleteDlg({ open: false, id: null, label: '' })}>
                <DialogTitle>Eliminar expediente</DialogTitle>
                <DialogContent>
                    <Typography>¿Estás seguro de eliminar el expediente <b>{deleteDlg.label}</b>?</Typography>
                    <Typography variant="body2" color="text.secondary" style={{ marginTop: 8 }}>Esta acción no se puede deshacer.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDlg({ open: false, id: null, label: '' })}>Cancelar</Button>
                    <Button onClick={handleEliminar} variant="contained" color="error" startIcon={<DeleteIcon />}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Selector de Listas */}
            <SelectorListas
                open={mostrarSelectorListas}
                onClose={() => setMostrarSelectorListas(false)}
                expedientesIds={expedientesSeleccionados}
                tipoExpediente={tipoExp}
                onSuccess={() => {
                    setExpedientesSeleccionados([]);
                    setMostrarSelectorListas(false);
                    setSnackbar({ open: true, msg: "Expedientes agregados a la lista", color: "success" });
                }}
            />
        </Box>
    );
}
